import { useState, useCallback } from 'react';
import { supabase } from '../lib/db';
import { useAuth } from '../components/AuthContext';
import toast from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';

export type UserRole = 'user' | 'seller' | 'provider';

export interface RoleProfiles {
  hasSellerProfile: boolean;
  hasProviderProfile: boolean;
  sellerDeactivated: boolean;
  providerDeactivated: boolean;
  sellerLocation: string;
  sellerPickupLocations: string[];
}

export interface UseRoleSwitcherReturn {
  /** Current state of fetched role profiles */
  profiles: RoleProfiles;
  /** Whether an async operation is in progress */
  loading: boolean;
  /** Which creation form should be shown ('' = none) */
  editingForm: '' | 'seller' | 'provider';
  /** Location state for SellerProfileForm */
  selectedLocation: string;
  setSelectedLocation: (loc: string) => void;
  /** Pickup locations state for SellerProfileForm */
  pickupLocations: string[];
  setPickupLocations: (locs: string[]) => void;
  /** Fetch the current user's seller/provider profile existence & status */
  fetchProfiles: () => Promise<void>;
  /** Switch to user role */
  switchToUser: () => Promise<void>;
  /** Switch to seller role (opens form if no seller profile exists) */
  switchToSeller: () => Promise<void>;
  /** Switch to provider role (opens form if no provider profile exists) */
  switchToProvider: () => Promise<void>;
  /** Handle seller profile form submission (insert or update) */
  submitSellerProfile: (data: any) => Promise<void>;
  /** Handle provider profile form submission (insert or update) */
  submitProviderProfile: (data: any) => Promise<void>;
  /** Close whatever creation form is open */
  closeForm: () => void;
  /** Toggle seller profile activation/deactivation */
  toggleSellerActivation: () => Promise<void>;
  /** Toggle provider profile activation/deactivation */
  toggleProviderActivation: () => Promise<void>;
}

export function useRoleSwitcher(): UseRoleSwitcherReturn {
  const { user, updateUserData } = useAuth();
  const { t } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [editingForm, setEditingForm] = useState<'' | 'seller' | 'provider'>('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [pickupLocations, setPickupLocations] = useState<string[]>([]);

  const [profiles, setProfiles] = useState<RoleProfiles>({
    hasSellerProfile: false,
    hasProviderProfile: false,
    sellerDeactivated: false,
    providerDeactivated: false,
    sellerLocation: '',
    sellerPickupLocations: [],
  });

  const updateRole = useCallback(async (newRole: UserRole) => {
    if (!user) return;
    const { error } = await supabase
      .from('profiles')
      .update({ role: newRole, updated_at: new Date().toISOString() })
      .eq('user_id', user.id);
    if (error) throw error;
    await updateUserData();
  }, [user, updateUserData]);

  const fetchProfiles = useCallback(async () => {
    if (!user) return;
    try {
      const [{ data: provider }, { data: seller }] = await Promise.all([
        supabase
          .from('provider_profiles')
          .select('id, is_deactivated')
          .eq('user_id', user.id)
          .limit(1)
          .maybeSingle(),
        supabase
          .from('seller_profiles')
          .select('id, location, pickup_locations, is_deactivated')
          .eq('user_id', user.id)
          .limit(1)
          .maybeSingle(),
      ]);

      setProfiles({
        hasProviderProfile: !!provider,
        hasSellerProfile: !!seller,
        providerDeactivated: provider?.is_deactivated || false,
        sellerDeactivated: seller?.is_deactivated || false,
        sellerLocation: seller?.location || '',
        sellerPickupLocations: seller?.pickup_locations || [],
      });

      if (seller) {
        setSelectedLocation(seller.location || '');
        setPickupLocations(seller.pickup_locations || []);
      }
    } catch (error) {
      console.error('Error fetching user profiles:', error);
    }
  }, [user]);

  const switchToUser = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      await updateRole('user');
    } catch (error) {
      console.error('Error switching to user:', error);
      toast.error(t('failedToSwitchRoles') || 'Failed to switch roles');
    } finally {
      setLoading(false);
    }
  }, [user, updateRole, t]);

  const switchToSeller = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: sellerProfile, error: sellerError } = await supabase
        .from('seller_profiles')
        .select('id, location, pickup_locations')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      if (sellerError) throw sellerError;

      if (!sellerProfile) {
        setEditingForm('seller');
        setLoading(false);
        return;
      }

      setSelectedLocation(sellerProfile.location || '');
      setPickupLocations(sellerProfile.pickup_locations || []);
      await updateRole('seller');
    } catch (error) {
      console.error('Error switching to seller:', error);
      toast.error(t('failedToSwitchRoles') || 'Failed to switch roles');
    } finally {
      setLoading(false);
    }
  }, [user, updateRole, t]);

  const switchToProvider = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: providerProfile, error: providerError } = await supabase
        .from('provider_profiles')
        .select('id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      if (providerError) throw providerError;

      if (!providerProfile) {
        setEditingForm('provider');
        setLoading(false);
        return;
      }

      await updateRole('provider');
    } catch (error) {
      console.error('Error switching to provider:', error);
      toast.error(t('failedToSwitchRoles') || 'Failed to switch roles');
    } finally {
      setLoading(false);
    }
  }, [user, updateRole, t]);

  const submitSellerProfile = useCallback(async (data: any) => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: existing } = await supabase
        .from('seller_profiles')
        .select('id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('seller_profiles')
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq('user_id', user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('seller_profiles')
          .insert([{ ...data, user_id: user.id, created_at: new Date().toISOString() }]);
        if (error) throw error;
      }

      setEditingForm('');
      await updateRole('seller');
      await fetchProfiles();
      toast.success(t('profileUpdateSuccess'));
    } catch (error) {
      console.error('Error saving seller profile:', error);
      toast.error(t('profileUpdateFailure'));
    } finally {
      setLoading(false);
    }
  }, [user, updateRole, fetchProfiles, t]);

  const submitProviderProfile = useCallback(async (data: any) => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: existing } = await supabase
        .from('provider_profiles')
        .select('id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('provider_profiles')
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq('user_id', user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('provider_profiles')
          .insert([{ ...data, user_id: user.id, created_at: new Date().toISOString() }]);
        if (error) throw error;
      }

      setEditingForm('');
      await updateRole('provider');
      await fetchProfiles();
      toast.success(t('profileUpdateSuccess'));
    } catch (error) {
      console.error('Error saving provider profile:', error);
      toast.error(t('profileUpdateFailure'));
    } finally {
      setLoading(false);
    }
  }, [user, updateRole, fetchProfiles, t]);

  const closeForm = useCallback(() => {
    setEditingForm('');
  }, []);

  const toggleSellerActivation = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const newDeactivated = !profiles.sellerDeactivated;
      const { error } = await supabase
        .from('seller_profiles')
        .update({ is_deactivated: newDeactivated, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
      if (error) throw error;

      toast.success(newDeactivated ? 'Seller profile deactivated' : 'Seller profile activated');

      // If activating and currently user role, switch to seller
      if (!newDeactivated && user.role === 'user') {
        await updateRole('seller');
      }
      // If deactivating and currently seller, switch back to user
      if (newDeactivated && user.role === 'seller') {
        await updateRole('user');
      }

      await fetchProfiles();
      await updateUserData();
    } catch (error) {
      console.error('Error toggling seller activation:', error);
      toast.error('Error updating profile status');
    } finally {
      setLoading(false);
    }
  }, [user, profiles.sellerDeactivated, updateRole, fetchProfiles, updateUserData]);

  const toggleProviderActivation = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const newDeactivated = !profiles.providerDeactivated;
      const { error } = await supabase
        .from('provider_profiles')
        .update({ is_deactivated: newDeactivated, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
      if (error) throw error;

      toast.success(newDeactivated ? 'Provider profile deactivated' : 'Provider profile activated');

      if (!newDeactivated && user.role === 'user') {
        await updateRole('provider');
      }
      if (newDeactivated && user.role === 'provider') {
        await updateRole('user');
      }

      await fetchProfiles();
      await updateUserData();
    } catch (error) {
      console.error('Error toggling provider activation:', error);
      toast.error('Error updating profile status');
    } finally {
      setLoading(false);
    }
  }, [user, profiles.providerDeactivated, updateRole, fetchProfiles, updateUserData]);

  return {
    profiles,
    loading,
    editingForm,
    selectedLocation,
    setSelectedLocation,
    pickupLocations,
    setPickupLocations,
    fetchProfiles,
    switchToUser,
    switchToSeller,
    switchToProvider,
    submitSellerProfile,
    submitProviderProfile,
    closeForm,
    toggleSellerActivation,
    toggleProviderActivation,
  };
}
